<template>
  <section class="tag-usage">
    <header class="tag-header">
      <div>
        <p class="breadcrumb">
          <RouterLink to="/">الرئيسية</RouterLink>
          <span>/</span>
          <RouterLink to="/wiki">الويكي</RouterLink>
          <span>/</span>
          <span v-if="usage">#{{ usage.tag.name }}</span>
        </p>
        <h1 v-if="usage">#{{ usage.tag.name }}</h1>
        <p v-if="usage" class="summary">
          يحتوي هذا الوسم على {{ usage.wiki_pages.length }} مقالات و {{ usage.employees.length }} سجلات موظفين.
        </p>
      </div>
    </header>

    <div v-if="loading" class="text-center text-muted py-6">...جار التحميل</div>

    <div v-else-if="error" class="alert alert-danger text-center py-6">
      {{ error }}
    </div>

    <div v-else-if="usage" class="usage-grid">
      <Card>
        <template #title>المقالات المرتبطة</template>
        <template #content>
          <div v-if="!usage.wiki_pages.length" class="text-muted">
            لا توجد مقالات مرتبطة بهذا الوسم بعد.
          </div>
          <ul v-else class="wiki-list">
            <li v-for="page in usage.wiki_pages" :key="page.page_id">
              <RouterLink :to="`/wiki/${page.slug}`">
                {{ page.title }}
              </RouterLink>
              <small class="meta">
                {{ page.event_date ? formatDate(page.event_date) : 'بدون تاريخ' }}
              </small>
            </li>
          </ul>
        </template>
      </Card>

      <Card>
        <template #title>الموظفون المرتبطون</template>
        <template #content>
          <div v-if="!usage.employees.length" class="text-muted">
            لا توجد سجلات موظفين مرتبطة بهذا الوسم.
          </div>
          <ul v-else class="employee-list">
            <li v-for="employee in usage.employees" :key="employee.employee_id">
              <RouterLink :to="`/employee/${employee.employee_id}`">
                {{ formatEmployeeName(employee) }}
              </RouterLink>
              <small class="meta">
                {{ formatLocation(employee) }}
              </small>
            </li>
          </ul>
        </template>
      </Card>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import Card from 'primevue/card'
import { fetchTagUsage, type TagUsageResponse } from '@/services/wiki'

const route = useRoute()
const usage = ref<TagUsageResponse | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

async function loadUsage() {
  loading.value = true
  usage.value = null
  error.value = null
  try {
    usage.value = await fetchTagUsage(route.params.slug as string)
  } catch (err: any) {
    console.error('Failed to load tag usage', err)
    error.value = err?.response?.data?.detail || 'تعذر تحميل بيانات الوسم.'
  } finally {
    loading.value = false
  }
}

function formatDate(value?: string | null) {
  if (!value) return 'غير محدد'
  return new Date(value).toLocaleDateString('ar-EG')
}

function formatEmployeeName(emp: TagUsageResponse['employees'][number]) {
  const full = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim()
  return full || `#${emp.employee_id}`
}

function formatLocation(emp: TagUsageResponse['employees'][number]) {
  return [emp.city, emp.state, emp.country].filter(Boolean).join('، ') || '—'
}

onMounted(loadUsage)
watch(() => route.params.slug, loadUsage)
</script>

<style scoped>
.tag-usage {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.tag-header .breadcrumb {
  display: flex;
  gap: 0.3rem;
  font-size: 0.9rem;
  color: var(--text-color-secondary, #64748b);
}
.tag-header h1 {
  margin: 0.4rem 0;
}
.usage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}
.wiki-list,
.employee-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.wiki-list li,
.employee-list li {
  display: flex;
  flex-direction: column;
}
.meta {
  color: var(--text-color-secondary, #94a3b8);
}
</style>
